'use client';

import { useId } from 'react';
import { Button2, IconButton2, Separator, Text } from '@frameio/vapor';
import { IconX } from '@frameio/vapor-icons';
import { LoadingContainer } from '@frameio/vapor-migration';
import styles from './ExportDialogContainer.module.css';
import TEXT from './generated/TEXT/ExportDialogContainer';

TEXT.DEFINE({
  /** Button label for exporting the sequence to Frame.io */
  exportToFrameio: 'Export to Frame.io',
  /** Button label to cancel the export process */
  cancel: 'Cancel',
  /** Button label to start the export process */
  export: 'Export',
  /** Label for the button to close the export modal */
  close: 'Close export modal',
});

type ExportDialogContainerProps = {
  children: React.ReactNode;
  error?: string;
  isLoading?: boolean;
  metadata?: React.ReactNode;
  onCancel: () => void;
  onSubmit: () => void;
  submitButtonLabel?: string;
};

export const ExportDialogContainer = ({
  children,
  error,
  isLoading = false,
  metadata,
  onCancel,
  onSubmit,
  submitButtonLabel,
}: ExportDialogContainerProps) => {
  return (
    <div className={styles.container}>
      <LoadingContainer
        borderRadius={8}
        className={styles.loadingContainer}
        height="auto"
        isLoading={isLoading}
        overflow="hidden"
      >
        <div className={styles.header}>
          <Text data-testid="export-modal-header" variant="primary">
            {TEXT.exportToFrameio}
          </Text>
          <IconButton2
            icon={<IconX />}
            label={TEXT.close}
            onPress={onCancel}
            variant="dimmed"
          />
        </div>
        <Separator />
        <div className={styles.body}>{children}</div>
      </LoadingContainer>
      <div className={styles.footer}>
        <Separator />
        {metadata}
        <ExportDialogFooter
          error={error}
          isLoading={isLoading}
          onCancel={onCancel}
          onExport={onSubmit}
          submitButtonLabel={submitButtonLabel}
        />
      </div>
    </div>
  );
};

const ExportDialogFooter = ({
  submitButtonLabel,
  onCancel,
  error,
  onExport,
  isLoading = false,
}: {
  submitButtonLabel?: string;
  onCancel: () => void;
  error?: string;
  onExport: () => void;
  isLoading?: boolean;
}) => {
  const errorId = useId();

  return (
    <>
      {error ? (
        <div className={styles.footerError}>
          <Text color="error" id={errorId} role="alert" variant="caption">
            {error}
          </Text>
        </div>
      ) : null}

      <div className={styles.footerActions}>
        <Button2 minWidth="50%" onPress={onCancel} variant="secondary">
          {TEXT.cancel}
        </Button2>

        <div className={styles.exportButtonWrapper}>
          <Button2
            aria-describedby={error ? errorId : undefined}
            aria-label={submitButtonLabel ?? TEXT.export}
            isDisabled={!!error}
            isLoading={isLoading}
            minWidth="100%"
            onPress={onExport}
            variant="primary"
          >
            {submitButtonLabel ?? TEXT.export}
          </Button2>
        </div>
      </div>
    </>
  );
};
